import React, { useState } from 'react';
import { useTheme, AlertBanner } from '@flowcore-water/ui';
import { useData } from '../../contexts/DataContext';
import { enrollmentService } from '../../services/enrollmentService';
import { SKILL_TREES, getDefaultDeadlineDays } from '../../data/skillTrees';
import { addDays } from '../../utils/timeline';
import type { Enrollment } from '../../types';

interface DeadlineEditorProps {
  enrollment: Enrollment;
  studentName: string;
  onClose: () => void;
  onSave: () => Promise<void>;
}

/** Convert a Date to YYYY-MM-DD string for date inputs */
const toDateStr = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Days between two dates (end - start), rounded */
const daysBetween = (start: Date, end: Date) =>
  Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

export const DeadlineEditor: React.FC<DeadlineEditorProps> = ({
  enrollment,
  studentName,
  onClose,
  onSave,
}) => {
  const { t } = useTheme();
  const { qualifications } = useData();
  const tree = SKILL_TREES.find((tr) => tr.id === enrollment.skillTreeId);

  // Store overrides as date strings (YYYY-MM-DD) keyed by qualId
  const [dateOverrides, setDateOverrides] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const [qualId, days] of Object.entries(enrollment.deadlineOverrides)) {
      initial[qualId] = toDateStr(addDays(enrollment.startDate, days));
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!tree) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const oldOverrides = enrollment.deadlineOverrides;
      // Convert date overrides back to days-from-start
      const newDaysOverrides: Record<string, number> = {};
      for (const [qualId, dateStr] of Object.entries(dateOverrides)) {
        newDaysOverrides[qualId] = daysBetween(enrollment.startDate, new Date(dateStr + 'T00:00:00'));
      }

      const allQuals = new Set([...Object.keys(oldOverrides), ...Object.keys(newDaysOverrides)]);

      for (const qualId of allQuals) {
        const hadOverride = qualId in oldOverrides;
        const hasOverride = qualId in newDaysOverrides;

        if (hasOverride && newDaysOverrides[qualId] !== oldOverrides[qualId]) {
          await enrollmentService.setDeadlineOverride(enrollment.id, qualId, newDaysOverrides[qualId]);
        } else if (hadOverride && !hasOverride) {
          await enrollmentService.clearDeadlineOverride(enrollment.id, qualId);
        }
      }
      await onSave();
    } catch (err) {
      console.error('Failed to save deadlines:', err);
      setError('Failed to save deadlines.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="rounded-lg border shadow-xl w-full max-w-2xl max-h-[80vh] overflow-auto"
        style={{ background: t.cardBg, borderColor: t.border }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {error && <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold" style={{ color: t.textPrimary }}>
                Deadline Configuration
              </h2>
              <p className="text-sm mt-1" style={{ color: t.textSecondary }}>
                {studentName} — {tree.name} — Started {formatDate(enrollment.startDate)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-xl font-bold px-2"
              style={{ color: t.textSecondary }}
            >
              &times;
            </button>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                <th className="pb-2 text-left font-medium" style={{ color: t.textSecondary }}>Qualification</th>
                <th className="pb-2 text-center font-medium" style={{ color: t.textSecondary }}>Default</th>
                <th className="pb-2 text-center font-medium" style={{ color: t.textSecondary }}>Deadline Date</th>
              </tr>
            </thead>
            <tbody>
              {tree.qualificationIds.map((qualId) => {
                const qual = qualifications.find((q) => q.id === qualId);
                const defaultDays = getDefaultDeadlineDays(tree.id, qualId);
                const defaultDate = defaultDays != null ? addDays(enrollment.startDate, defaultDays) : null;
                const hasOverride = qualId in dateOverrides;

                return (
                  <tr
                    key={qualId}
                    style={{ borderBottom: `1px solid ${t.borderSubtle}` }}
                  >
                    <td className="py-3" style={{ color: t.textPrimary }}>
                      <div className="font-medium">{qual?.shortName || qualId}</div>
                      {qual?.optional && (
                        <span className="text-xs" style={{ color: t.textSecondary }}>Optional</span>
                      )}
                    </td>
                    <td className="py-3 text-center" style={{ color: t.textSecondary }}>
                      {defaultDate ? formatDate(defaultDate) : '—'}
                    </td>
                    <td className="py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="date"
                          value={hasOverride ? dateOverrides[qualId] : ''}
                          min={toDateStr(enrollment.startDate)}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) {
                              const next = { ...dateOverrides };
                              delete next[qualId];
                              setDateOverrides(next);
                            } else {
                              setDateOverrides({ ...dateOverrides, [qualId]: val });
                            }
                          }}
                          className="rounded border px-2 py-1 text-sm"
                          style={{
                            background: t.inputBg,
                            borderColor: hasOverride ? t.accent : t.inputBorder,
                            color: t.textPrimary,
                            colorScheme: 'dark',
                          }}
                          placeholder={defaultDate ? toDateStr(defaultDate) : ''}
                        />
                        {hasOverride && (
                          <button
                            onClick={() => {
                              const next = { ...dateOverrides };
                              delete next[qualId];
                              setDateOverrides(next);
                            }}
                            className="text-xs"
                            style={{ color: t.textSecondary }}
                            title="Reset to default"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-end gap-3 mt-6 pt-4" style={{ borderTop: `1px solid ${t.border}` }}>
            <button
              onClick={onClose}
              className="rounded-lg border px-4 py-2 text-sm font-medium"
              style={{ background: t.buttonBg, borderColor: t.buttonBorder, color: t.buttonText }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              style={{ background: t.accent, color: t.pageBg }}
            >
              {saving ? 'Saving...' : 'Save Deadlines'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
